import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { PageHeader } from '../components/Layout';
import { Button, Card, Grid, KV, Metric, Pill, SectionHeader } from '../components/ui';
import { StatusCard } from '../components/ops/StatusCard';
import { SwitchesTable } from '../components/ops/SwitchesTable';
import { AuditTable } from '../components/ops/AuditTable';
import { loadOpsOverview, type HealthKey, type OverviewPayload } from '../lib/opsOverview';
import { boolTone, enabledLabel } from '../lib/opsStatus';
import { COLORS } from '../theme';

// Mission Control Phase 1 — private, read-only Operations Console.
// Five sections: System Health, Runtime Switches, Recent Activity,
// Infrastructure, Quick Statistics. No editing, no polling, no realtime.

const HEALTH_ORDER: Array<{ key: HealthKey; label: string }> = [
  { key: 'app',        label: 'App' },
  { key: 'api',        label: 'API' },
  { key: 'database',   label: 'Database' },
  { key: 'google',     label: 'Google API' },
  { key: 'openai',     label: 'OpenAI' },
  { key: 'revenuecat', label: 'RevenueCat' },
  { key: 'sentry',     label: 'Sentry' },
];

export default function OperationsPage() {
  const [data, setData] = useState<OverviewPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await loadOpsOverview());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load operations overview.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <div style={stateStyle}>Loading operations overview…</div>;
  if (error) {
    return (
      <div style={stateStyle}>
        {error}{' '}
        <Button onClick={() => { setLoading(true); void load(); }}>Retry</Button>
      </div>
    );
  }
  if (!data) return null;

  const { health, switches, audit, infrastructure: infra, stats } = data;

  return (
    <div>
      <PageHeader
        title="Operations"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
              As of {new Date(data.asOf).toLocaleString()}
            </span>
            <Button variant="ghost" onClick={() => { setLoading(true); void load(); }}>Refresh</Button>
          </div>
        }
      />

      {/* 5 — Quick Statistics (overview strip) */}
      <Card title="Quick Statistics">
        <Grid cols={4}>
          <Metric label="Switches"          value={stats.totalSwitches} />
          <Metric label="Active temporary"  value={stats.activeTemporary} color={stats.activeTemporary ? COLORS.info : undefined} />
          <Metric label="Expired pending"   value={stats.expiredPending} color={stats.expiredPending ? COLORS.warn : undefined} />
          <Metric label="Audit today"       value={stats.auditToday} />
        </Grid>
      </Card>

      {/* 1 — System Health */}
      <SectionHeader>System Health</SectionHeader>
      <Grid cols={4}>
        {HEALTH_ORDER.map(({ key, label }) => (
          <StatusCard key={key} label={label} status={health[key]} />
        ))}
      </Grid>

      {/* 4 — Infrastructure */}
      <SectionHeader>Infrastructure</SectionHeader>
      <Grid cols={2}>
        <Card>
          <KV k="Current app version" v={infra.currentAppVersion ?? '—'} color={COLORS.textMuted} />
          <KV k="Minimum supported (iOS)" v={infra.minimumSupportedVersion.ios} />
          <KV k="Minimum supported (Android)" v={infra.minimumSupportedVersion.android} />
        </Card>
        <Card>
          <KV k="Maintenance (full)" v={infra.maintenanceFull ? 'ON' : 'off'} color={infra.maintenanceFull ? COLORS.critical : COLORS.textSecondary} />
          <KV k="Maintenance banner" v={infra.maintenanceBanner ? 'shown' : 'hidden'} color={infra.maintenanceBanner ? COLORS.warn : COLORS.textSecondary} />
          <KV k="Degraded mode" v={enabledLabel(infra.degradedMode)} color={infra.degradedMode ? COLORS.warn : COLORS.textSecondary} />
          <KV k="Replies enabled" v={enabledLabel(infra.repliesEnabled)} color={boolTone(infra.repliesEnabled)} />
          <KV k="Google sync enabled" v={enabledLabel(infra.googleSyncEnabled)} color={boolTone(infra.googleSyncEnabled)} />
        </Card>
      </Grid>

      {/* 2 — Runtime Switches */}
      <SectionHeader right={<Pill>read-only</Pill>}>Runtime Switches</SectionHeader>
      <SwitchesTable rows={switches} />

      {/* 3 — Recent Activity */}
      <SectionHeader right={<Pill>{audit.length} recent</Pill>}>Recent Activity</SectionHeader>
      <AuditTable rows={audit} />
    </div>
  );
}

const stateStyle: CSSProperties = { color: COLORS.textSecondary, fontSize: 14, padding: 40 };
